import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { AuthApiService } from '../services/auth-api.service';
import { AuthServerClockService } from '../services/auth-server-clock.service';
import { SessionService } from '../services/session.service';
import { JwtUtil } from '@ribera-web/token-decodifier-service';

export const authGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const jwtUtil = inject(JwtUtil);
  const clock = inject(AuthServerClockService);
  const authApi = inject(AuthApiService);
  const sessionService = inject(SessionService);

  const tokenFromUrl = route.queryParams['token'];
  
  if (tokenFromUrl) {
    localStorage.setItem('accessToken', tokenFromUrl);

    setTimeout(() => {
      router.navigate([], {
        queryParams: { token: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }, 500);
  }

  const token = localStorage.getItem('accessToken');

  if (!token) {
    const currentUrl = state.url;
    
    return router.createUrlTree(['/auth/login'], {
      queryParams: { returnUrl: currentUrl }
    });
  }

  try {
    const expiration = jwtUtil.getExpirationDate(token);
    const expired = clock.isExpired(expiration);
    if (expired === true) {
      localStorage.removeItem('accessToken');
      return router.parseUrl('/auth/login');
    }
    if (expired === false) return true;

    // After a reload performance.now() has a new origin. Verify with Security MS,
    // never guess token expiry from a modified Windows clock.
    return authApi.refreshToken().pipe(
      map(response => {
        const refreshed = response.data?.access_token;
        if (!refreshed || !response.timestamp) throw new Error('Missing refreshed session');
        clock.synchronize(response.timestamp);
        if (clock.isExpired(jwtUtil.getExpirationDate(refreshed)) !== false) {
          throw new Error('Expired refreshed session');
        }
        localStorage.setItem('accessToken', refreshed);
        sessionService.startSessionTimer(refreshed);
        return true;
      }),
      catchError(() => {
        localStorage.removeItem('accessToken');
        return of(router.parseUrl('/auth/login'));
      })
    );

  } catch (error) {
    localStorage.removeItem('accessToken');
    return router.parseUrl('/auth/login');
  }
};